export type DoubleShift = "DAY" | "NIGHT";
export type TripleShift = "DAY" | "AFTERNOON" | "NIGHT";

/** The letter the API carries a shift as, matching tblShift.Shift and appsettings' ShiftBands. */
export type ShiftLetter = "D" | "A" | "N";

const NAME_BY_LETTER: Record<ShiftLetter, TripleShift> = {
  D: "DAY",
  A: "AFTERNOON",
  N: "NIGHT",
};

const LETTER_BY_NAME: Record<TripleShift, ShiftLetter> = {
  DAY: "D",
  AFTERNOON: "A",
  NIGHT: "N",
};

/**
 * Shift ordinal within a day, matching the configured bands' ShiftNumber. Used to rank two shifts on the
 * same date - a night shift is later than that day's afternoon, which is later than its day shift.
 */
export const SHIFT_ORDINAL: Record<ShiftLetter, number> = { D: 0, A: 1, N: 2 };
export const SHIFTS_PER_DAY = 3;

export function isShiftLetter(value: string | null | undefined): value is ShiftLetter {
  return value === "D" || value === "A" || value === "N";
}

/**
 * The API's shift letter as the name the app carries internally.
 *
 * Note: nine call sites wrote this mapping inline as `x === 'D' ? 'DAY' : 'NIGHT'` or
 * `x === 'N' ? 'NIGHT' : 'DAY'`, both of which turn an afternoon shift into the wrong one. In the Ore
 * Tally modal that meant opening an afternoon entry read and wrote the DAY shift's rows. An unrecognised
 * letter still falls back to DAY, but the API only ever sends D, A or N, so that is now genuinely a
 * malformed-input path rather than the afternoon shift's normal route.
 */
export function shiftLetterToName(letter: string | null | undefined): TripleShift {
  const normalised = (letter ?? "").trim().toUpperCase().charAt(0);
  return isShiftLetter(normalised) ? NAME_BY_LETTER[normalised] : "DAY";
}

/** The shift letter for a name. Prefer this over `name[0]`, which is only right by coincidence. */
export function shiftNameToLetter(name: TripleShift | DoubleShift): ShiftLetter {
  return LETTER_BY_NAME[name as TripleShift];
}
